import { DecisionWithId } from '@livv/models';
import { Box, Container, Link, List, ListItem, SxProps } from '@mui/material';
import { NextPage } from 'next';
import Head from 'next/head';
import useTranslation from 'next-translate/useTranslation';
import HeaderBar from '@components/HeaderBar';
import { ResultsCount } from '@components/search';
import PaginationPublicDecisions from '@screens/Sitemap/decisions/PaginationPublicDecisions';
import PreviousNextYearLinks from '@screens/Sitemap/decisions/PreviousNextYearLinks';
import { MARGIN_BEFORE_FOOTER } from '@styles/consts';
import { APP_URL } from '@utils/consts/livv';

export interface YearPublicDecisionsPageProps {
    decisions: DecisionWithId[];
    page: string;
    year: string;
}

export type PaginationPublicDecisionsPageProps = {
    totalDecisionsCount: number;
    totalPages: number;
};

export type PreviousNextYearPageProps = {
    nextYear: string;
    previousYear: string;
};

export interface PublicDecisionsPageProps
    extends PaginationPublicDecisionsPageProps,
        PreviousNextYearPageProps,
        YearPublicDecisionsPageProps {}

const decisionLinkSx: SxProps = {
    '&:hover': {
        textDecoration: 'underline',
    },
    color: 'grey.100',
    fontWeight: '600',
    textDecoration: 'none',
};

const YearPublicDecisionsPage: NextPage<PublicDecisionsPageProps> = ({
    decisions,
    nextYear,
    page = '1',
    previousYear,
    totalDecisionsCount,
    totalPages,
    year,
}) => {
    const { t } = useTranslation('sitemap');
    const pageParsed = parseInt(page, 10);
    const isMoreThanOnePage = totalPages > 1;
    const isFirstPage = page === '1';
    const canonicalUrl = `https://${APP_URL}/sitemap/decisions/${year}${
        isFirstPage ? '' : `?page=${page}`
    }`;
    const metaTitle = isFirstPage
        ? t('pageTitle.byYearWithoutPage', { year })
        : t('pageTitle.byYearWithPage', { page, year });
    const metaDescription = isFirstPage
        ? t('meta.descriptionWithoutPage', { year })
        : t('meta.descriptionWithPage', { page, year });

    return (
        <>
            <HeaderBar
                subtitle={t('headerBar.yearDecision.subtitle')}
                subtitleHref="/sitemap/decisions"
                title={t('headerBar.yearDecision.title', { page, year })}
            />
            <Head>
                <title>{metaTitle}</title>
                <meta content={metaDescription} name="description" />
                <meta content="index, follow" name="robots" />
                <link href={canonicalUrl} rel="canonical" />
            </Head>
            <Container maxWidth="md" sx={{ mb: MARGIN_BEFORE_FOOTER }}>
                <Box
                    alignItems="center"
                    display="flex"
                    justifyContent="space-between"
                    sx={{ mt: 4 }}
                >
                    <ResultsCount nbResults={totalDecisionsCount} />
                    {isMoreThanOnePage && (
                        <PaginationPublicDecisions
                            page={pageParsed}
                            totalPages={totalPages}
                            year={year}
                        />
                    )}
                </Box>
                <List sx={{ py: 2 }}>
                    {decisions.map(({ id, title }) => (
                        <ListItem key={id} sx={{ pl: 0 }}>
                            <Link
                                href={`/decisions/${id}`}
                                rel="noopener noreferrer"
                                sx={decisionLinkSx}
                                target="_blank"
                            >
                                {title}
                            </Link>
                        </ListItem>
                    ))}
                </List>
                <Box alignItems="center" display="flex" justifyContent="end">
                    {isMoreThanOnePage && (
                        <PaginationPublicDecisions
                            page={pageParsed}
                            totalPages={totalPages}
                            year={year}
                        />
                    )}
                </Box>
                <PreviousNextYearLinks nextYear={nextYear} previousYear={previousYear} />
            </Container>
        </>
    );
};

export default YearPublicDecisionsPage;
